import { useCallback, useEffect, useState } from 'react';
import MapOverlay from '@alltrails/amplitude/enums/MapOverlay';
import PoiActionDetailedLocation from '@alltrails/amplitude/enums/PoiActionDetailedLocation';
import logExplorePOIViewViewed from '@alltrails/amplitude/events/logExplorePOIViewViewed';
import logPOISelected from '@alltrails/amplitude/events/logPOISelected';
import useMap from '@alltrails/maps/hooks/useMap';
import { getMapBaseLayer, getMapOverlay } from '@alltrails/maps/utils/amplitudeHelpers';
import type { POISearchResult } from '@alltrails/types/AlgoliaResults';
import { getFilterNames } from './analyticsHelpers';
import { useAppSelector } from './redux';

export function useLogPOISelected() {
  const map = useMap();
  const { baseStyleId, distanceAwayIsochrone, dynamicStyleIds, exploreServiceFilters, staticStyleIds, trailEngagement } = useAppSelector(state => ({
    baseStyleId: state.map.baseStyleId,
    distanceAwayIsochrone: state.exploreService.distanceAwayIsochrone,
    dynamicStyleIds: state.map.dynamicStyleIds,
    exploreServiceFilters: state.exploreService.filters,
    staticStyleIds: state.map.staticStyleIds,
    trailEngagement: state.exploreService.trailEngagement
  }));

  return useCallback(
    (poi: POISearchResult, poiDetailedLocation: PoiActionDetailedLocation) => {
      logPOISelected({
        filters_applied: getFilterNames(exploreServiceFilters, Boolean(distanceAwayIsochrone), Boolean(trailEngagement.length)),
        lat: map?.getCenter().lat,
        lng: map?.getCenter().lng,
        map_base_layer: getMapBaseLayer(baseStyleId),
        map_overlay: [...dynamicStyleIds, ...staticStyleIds].map(id => getMapOverlay(id) as MapOverlay),
        poi_detailed_location: poiDetailedLocation,
        poi_id: poi.ID,
        poi_location: poiDetailedLocation === 'explore map default state' ? 'explore map' : 'custom route builder',
        poi_type_raw: poi.subtype,
        poi_source: 'poi'
      });
    },
    [baseStyleId, distanceAwayIsochrone, dynamicStyleIds, exploreServiceFilters, map, staticStyleIds, trailEngagement.length]
  );
}

export function useLogExplorePOIViewViewed(poi: POISearchResult) {
  const [hasLogged, setHasLogged] = useState(false);
  const map = useMap();
  const { baseStyleId, distanceAwayIsochrone, dynamicStyleIds, exploreServiceFilters, staticStyleIds, searchResultsSummary, trailEngagement } =
    useAppSelector(state => ({
      baseStyleId: state.map.baseStyleId,
      distanceAwayIsochrone: state.exploreService.distanceAwayIsochrone,
      dynamicStyleIds: state.map.dynamicStyleIds,
      exploreServiceFilters: state.exploreService.filters,
      staticStyleIds: state.map.staticStyleIds,
      searchResultsSummary: state.exploreService.searchResultsSummary,
      trailEngagement: state.exploreService.trailEngagement
    }));

  useEffect(() => {
    if (!hasLogged && map && searchResultsSummary) {
      const { lat, lng } = map.getCenter();
      logExplorePOIViewViewed({
        filters_applied: getFilterNames(exploreServiceFilters, Boolean(distanceAwayIsochrone), Boolean(trailEngagement.length)),
        lat,
        lng,
        map_base_layer: getMapBaseLayer(baseStyleId),
        map_overlay: [...dynamicStyleIds, ...staticStyleIds].map(id => getMapOverlay(id) as MapOverlay),
        num_results_returned: searchResultsSummary.count,
        poi_id: poi.ID,
        zoom_level: map.getZoom()
      });
      setHasLogged(true);
    }
  }, [
    baseStyleId,
    distanceAwayIsochrone,
    dynamicStyleIds,
    exploreServiceFilters,
    hasLogged,
    map,
    poi.ID,
    staticStyleIds,
    searchResultsSummary,
    trailEngagement.length
  ]);

  useEffect(() => {
    // Retrigger the event if the overlays change
    setHasLogged(false);
  }, [dynamicStyleIds, staticStyleIds]);
}
